import { useState } from "react";
import { Link } from "wouter";
import ScreenHeader from "@/components/mnf/ScreenHeader";
import Icon from "@/components/mnf/Icon";
import { GlassCard, PillTag, Btn, Avatar, SectionTitle } from "@/components/mnf/UI";
import { useQuery as useConvexQuery, useMutation as useConvexMutation } from "convex/react";
import {
  useStoreQuery,
  queries,
  type MicronutrientPanelKey,
} from "@/lib/store";
import { reportWriteFailure } from "@/lib/reportWriteFailure";
import { api } from "../../../convex/_generated/api";

/**
 * /kileys-pick — "Kiley's Pick for Your Season"
 *
 * Coach Kiley hand-picks one recipe per day for the sister's life season
 * (prenatal, nursing, or general). The pick rotates by day-of-year so the
 * recommendation stays put across reloads. This is curated by hand, not
 * generated. The whole point: faith customers trust their coach, not an
 * algorithm. Coach voice carries the recommendation, scripture grounds it,
 * focus line tells her what to look for at the table.
 */

const PANEL_LABEL: Record<MicronutrientPanelKey, string> = {
  prenatal: "for your prenatal season",
  nursing: "for your nursing season",
  general: "for everyday wellness",
};

const PANEL_EYEBROW: Record<MicronutrientPanelKey, string> = {
  prenatal: "Prenatal pick",
  nursing: "Nursing pick",
  general: "Wellness pick",
};

export default function KileysPick() {
  const me = useStoreQuery(queries.currentUser);
  // No fallback to the seeded demo account: when unresolved, both queries return null
  // below, and the `if (!bundle)` guard further down already renders a
  // "pick is on the way" state instead of reading Hannah's season pick.
  const userId = me?.id ?? null;

  const todayBundle = useStoreQuery(() => (userId ? queries.kileySeasonPickForUser(userId) : null));
  const tomorrowBundle = useStoreQuery(() => (userId ? queries.tomorrowsKileyPick(userId) : null));

  const [showTomorrow, setShowTomorrow] = useState(false);
  const bundle = showTomorrow ? tomorrowBundle : todayBundle;

  const otherPicks = useStoreQuery(() =>
    bundle ? queries.kileySeasonPicksByPanel(bundle.panelKey) : [],
  );

  // Saved state lives in Convex, same as RecipeDetail and Nourish. This page
  // used the in-memory store's savedRecipes, whose ONLY reader was this page --
  // so the heart filled here and the recipe never appeared in her Saved shelf,
  // and opening the same recipe from Nourish showed it unsaved.
  const isSaved = useConvexQuery(
    api.recipesDb.isSavedBy,
    bundle && me?.id ? { userId: me.id, recipeId: bundle.recipe.id } : "skip",
  ) ?? false;

  const saveRecipe = useConvexMutation(api.recipesDb.saveRecipe);
  const unsaveRecipe = useConvexMutation(api.recipesDb.unsaveRecipe);

  if (!bundle) {
    return (
      <div className="pb-24">
        <ScreenHeader
          variant="cream"
          eyebrow="Kiley's Pick"
          title="For your season"
          subtitle="A recipe is on the way, sister."
        />
      </div>
    );
  }

  const { pick, recipe, panelKey } = bundle;

  const handleToggleSave = () => {
    if (!me?.id) return;
    const args = { userId: me.id, recipeId: recipe.id };
    if (isSaved) {
      void unsaveRecipe(args).catch((err: unknown) => {
        reportWriteFailure(
          "kileys-pick", "unsaving a recipe", err,
          "We couldn't unsave this",
          "It's still in your saved recipes. Check your connection and try again.",
        );
      });
    } else {
      void saveRecipe(args).catch((err: unknown) => {
        reportWriteFailure(
          "kileys-pick", "saving a recipe", err,
          "We couldn't save this recipe",
          "It didn't make it to your saved recipes. Check your connection and tap the heart again.",
        );
      });
    }
  };

  const filteredOthers = otherPicks.filter((o) => o.pick.id !== pick.id);

  return (
    <div className="pb-24">
      <ScreenHeader
        variant="cream"
        eyebrow={PANEL_EYEBROW[panelKey]}
        title={showTomorrow ? "A peek at tomorrow" : "For your season today"}
        subtitle={
          showTomorrow
            ? "A glimpse, sister. Today's pick is still where you should land."
            : "Hand-picked for you by Coach Kiley."
        }
      />

      <div className="px-5 mt-2 space-y-4">
        {/* Hero recipe card */}
        <Link
          href={`/nourish/${recipe.id}`}
          className="no-underline block"
          data-testid="card-kiley-pick"
        >
          <GlassCard organic padding={0} style={{ overflow: "hidden", cursor: "pointer" }}>
            <div
              className="relative"
              style={{
                background: recipe.heroGradient,
                minHeight: 168,
                padding: "18px 20px",
                color: "#F5F0E8",
              }}
            >
              <div
                aria-hidden
                style={{
                  position: "absolute",
                  right: 18,
                  bottom: 12,
                  fontSize: 56,
                  opacity: 0.55,
                  letterSpacing: "0.18em",
                }}
              >
                {recipe.heroEmojis.slice(0, 2).join(" ")}
              </div>
              <p
                className="mnf-eyebrow"
                style={{ color: "rgba(245,240,232,0.85)", marginBottom: 6 }}
              >
                Kiley's Pick {PANEL_LABEL[panelKey]}
              </p>
              <h2
                className="mnf-serif"
                style={{
                  fontSize: 24,
                  fontWeight: 500,
                  color: "#F5F0E8",
                  letterSpacing: "-0.01em",
                  lineHeight: 1.15,
                  maxWidth: 280,
                }}
                data-testid="text-pick-recipe-title"
              >
                {recipe.title}
              </h2>
              <p
                className="mnf-body"
                style={{
                  fontSize: 13,
                  color: "rgba(245,240,232,0.85)",
                  marginTop: 8,
                  lineHeight: 1.5,
                  maxWidth: 280,
                }}
              >
                {recipe.description}
              </p>
            </div>
            <div className="px-5 py-3 flex items-center gap-2 flex-wrap">
              <PillTag tone="forest">{recipe.kcal} kcal</PillTag>
              <PillTag tone="coral">{recipe.proteinG}g protein</PillTag>
              <PillTag tone="neutral">{recipe.durationMin} min</PillTag>
              {recipe.fiberG > 0 && (
                <PillTag tone="fiber">{recipe.fiberG}g fiber</PillTag>
              )}
            </div>
          </GlassCard>
        </Link>

        {/* Note from Kiley */}
        <GlassCard
          organic
          padding={20}
          style={{
            background:
              "linear-gradient(135deg, rgba(192,120,74,0.10) 0%, rgba(245,240,232,0.85) 100%)",
            border: "1px solid rgba(160,120,32,0.22)",
          }}
        >
          <div className="flex items-start gap-3">
            <Avatar initials="KM" size={48} ringColor="rgba(160,120,32,0.55)" />
            <div className="flex-1 min-w-0">
              <p
                className="mnf-eyebrow"
                style={{ color: "var(--mnf-gold, #A07820)", marginBottom: 2 }}
              >
                A note from Kiley
              </p>
              <p
                className="mnf-serif"
                style={{
                  fontSize: 15,
                  fontWeight: 500,
                  color: "var(--mnf-fd)",
                  lineHeight: 1.4,
                }}
              >
                Coach Kiley · Flourishing
              </p>
            </div>
          </div>

          <p
            className="mnf-body mt-4"
            style={{
              fontSize: 14.5,
              lineHeight: 1.6,
              color: "var(--mnf-body)",
            }}
            data-testid="text-kiley-note"
          >
            {pick.kileyNote}
          </p>

          <div
            className="mt-4 pt-4"
            style={{ borderTop: "1px solid rgba(30,74,53,0.14)" }}
          >
            <p
              className="mnf-eyebrow"
              style={{ color: "var(--mnf-t)", marginBottom: 4 }}
            >
              Anchor verse
            </p>
            <p
              className="mnf-serif italic"
              style={{
                fontSize: 14,
                lineHeight: 1.55,
                color: "var(--mnf-fd)",
                fontWeight: 500,
              }}
            >
              "{pick.scripture}"
            </p>
            <p
              className="mnf-body"
              style={{
                fontSize: 12,
                color: "var(--mnf-tg)",
                marginTop: 4,
                letterSpacing: "0.02em",
              }}
            >
              {pick.scriptureRef}
            </p>
          </div>

          <div
            className="mt-4 pt-4"
            style={{ borderTop: "1px solid rgba(30,74,53,0.14)" }}
          >
            <p
              className="mnf-eyebrow"
              style={{ color: "var(--mnf-t)", marginBottom: 4 }}
            >
              At the table
            </p>
            <p
              className="mnf-body"
              style={{
                fontSize: 13.5,
                lineHeight: 1.55,
                color: "var(--mnf-body)",
              }}
            >
              {pick.focus}
            </p>
          </div>
        </GlassCard>

        {/* CTAs */}
        <div className="flex flex-col gap-3">
          <Link href={`/nourish/${recipe.id}`} className="no-underline">
            <Btn variant="primary" size="lg" block data-testid="button-open-recipe">
              Open the recipe
            </Btn>
          </Link>
          <Btn
            variant={isSaved ? "secondary" : "coral"}
            size="md"
            block
            onClick={handleToggleSave}
            data-testid="button-save-pick"
          >
            {isSaved ? "Saved to Garden 🌱" : "Add to Garden 🌱"}
          </Btn>
          <Btn
            variant="ghost"
            size="md"
            block
            onClick={() => setShowTomorrow((v) => !v)}
            data-testid="button-peek-tomorrow"
          >
            {showTomorrow ? "Back to today's pick" : "Peek tomorrow"}
          </Btn>
        </div>

        {/* Other picks for this season */}
        {filteredOthers.length > 0 && (
          <>
            <SectionTitle
              eyebrow="More from Kiley"
              title="Other picks for your season"
            />
            <div className="space-y-2">
              {filteredOthers.map(({ pick: op, recipe: r }) => (
                <Link
                  key={op.id}
                  href={`/nourish/${r.id}`}
                  className="no-underline block"
                  data-testid={`link-other-pick-${op.id}`}
                >
                  <GlassCard organic padding={14} style={{ cursor: "pointer" }}>
                    <div className="flex items-center gap-3">
                      <div
                        aria-hidden
                        className="shrink-0 inline-flex items-center justify-center"
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: "16px 6px 16px 6px",
                          background: r.heroGradient,
                          color: "#F5F0E8",
                          fontSize: 22,
                        }}
                      >
                        {r.heroEmojis[0] ?? "🌿"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p
                          className="mnf-serif"
                          style={{
                            fontSize: 15,
                            fontWeight: 500,
                            color: "var(--mnf-fd)",
                            lineHeight: 1.3,
                          }}
                        >
                          {r.title}
                        </p>
                        <p
                          className="mnf-body"
                          style={{
                            fontSize: 12,
                            color: "var(--mnf-tg)",
                            marginTop: 2,
                            lineHeight: 1.4,
                          }}
                        >
                          {op.focus}
                        </p>
                      </div>
                      <Icon
                        name="chevron_right"
                        size={20}
                        weight={400}
                        color="var(--mnf-tg)"
                      />
                    </div>
                  </GlassCard>
                </Link>
              ))}
            </div>
          </>
        )}

        <p
          className="mnf-body italic text-center"
          style={{ fontSize: 12, color: "var(--mnf-tg)", marginTop: 12 }}
        >
          Hand-picked by Coach Kiley. No algorithms here, just a coach who knows you.
        </p>
      </div>
    </div>
  );
}
